import { ConfluenceSpaceAri } from '@atlassian/ari';
import React, { createContext, useCallback, useEffect, useState } from 'react';

type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export interface DatabaseContent {
  cloudId: string;
  contentId: string | null;
  entryId: string | null;
  fieldId: string | null;
  savedViewId: string | null;
  unsavedViewId: string | null;
  activationId: string | null;
  referencing: boolean;
  setContentId: (contentId: string) => void;
  spaceKey: string;
  referenceId: string | null;
  spaceId: string | null;
  /** The content ID of the database parent in Confluence, i.e. where the database is
   * embedded. In standalone, it's just the content ID of the database. It's
   * null if it's embedded not in Confluence or cross-origin.  */
  parentContentId: string | null;

  spaceName?: string;
  title?: string;
}

export const DatabaseContentContext = createContext<DatabaseContent>({
  cloudId: 'error',
  contentId: null,
  entryId: null,
  fieldId: null,
  savedViewId: null,
  unsavedViewId: null,
  activationId: null,
  referencing: false,
  setContentId: () => {},
  spaceKey: 'error',
  referenceId: null,
  spaceId: null,
  parentContentId: null,
});

/**
 * Get the top window in the window hierarchy.
 */
export function getParentWindow(): Window {
  try {
    let currentWindow: Window = window;
    while (
      currentWindow.location.toString() !== currentWindow.parent.location.toString()
    ) {
      currentWindow = currentWindow.parent;
    }
    return currentWindow;
  } catch {
    // If we're in an iframe and the parent is on a different domain, we can't access it.
    // In this case we fall back to the current window. This covers the case where the app is
    // embedded in a different domain (e.g. in a different tenant) via smartlink.
    return window;
  }
}

const getUrlSearchParams = (mode: 'self' | 'parent') => {
  if (mode === 'self') {
    return new URLSearchParams(window.location.search);
  }

  return new URLSearchParams(getParentWindow().location.search);
};

const isDatabaseContent = (
  params: DatabaseContent | Nullable<Pick<DatabaseContent, 'cloudId' | 'spaceKey'>>
): params is DatabaseContent => {
  return !!params.cloudId && !!params.spaceKey;
};

export const QueryParamsContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const selfQueryParams = getUrlSearchParams('self');
  const parentQueryParams = getUrlSearchParams('parent');

  const [contentId, setContentId] = useState(selfQueryParams.get('contentId'));
  const cloudId = selfQueryParams.get('cloudId');
  const activationId = selfQueryParams.get('activationId');
  const spaceKey = selfQueryParams.get('spaceKey');
  const [entryId, setEntryId] = useState<string | null>(null);
  const [fieldId, setFieldId] = useState<string | null>(null);
  const [savedViewId, setSavedViewId] = useState<string | null>(null);
  const [unsavedViewId, setUnsavedViewId] = useState<string | null>(null);
  const [referencing, setReferencing] = useState<boolean>(false);
  const referenceId = selfQueryParams.get('databaseId'); // mismatch intentional
  const containerAri = selfQueryParams.get('containerAri');
  let spaceId: null | string = null;
  if (containerAri) {
    spaceId = ConfluenceSpaceAri.parse(containerAri).spaceId;
  }

  const [parentContentId, setParentContentId] = useState<string | null>(null);

  const value = React.useMemo(
    () => ({
      contentId,
      entryId,
      fieldId,
      savedViewId,
      unsavedViewId,
      referencing,
      cloudId,
      activationId,
      setContentId,
      spaceKey,
      referenceId,
      spaceId,
      parentContentId,
    }),
    [
      contentId,
      entryId,
      fieldId,
      referencing,
      savedViewId,
      unsavedViewId,
      cloudId,
      activationId,
      setContentId,
      spaceKey,
      referenceId,
      spaceId,
      parentContentId,
    ]
  );

  const getParentContentId = useCallback(() => {
    const pattern = /^\/wiki\/spaces\/([^/]+)\/([^/]+\/?[^/]*)\/(\d+)(\/.*)?$/;

    try {
      // whiteboards and embeds are in iframes, so we need to check
      // window.parent.parent
      const pathsToCheck =
        typeof window !== 'undefined'
          ? [window.parent.location.pathname, window.parent.parent.location.pathname]
          : [];

      for (const path of pathsToCheck) {
        const match = path.match(pattern);
        if (match) {
          const contentId = decodeURIComponent(match[3]);
          return setParentContentId(contentId);
        }
      }

      // URL won't match if not in confluence
      return setParentContentId(null);
    } catch {
      // can't access window.parent if cross-origin
      return setParentContentId(null);
    }
  }, []);

  const getContentData = useCallback(() => {
    // check for entryId in parent window and self window
    if (parentQueryParams.has('entryId')) {
      setEntryId(parentQueryParams.get('entryId') as string);
    }
    if (selfQueryParams.has('entryId')) {
      setEntryId(selfQueryParams.get('entryId') as string);
    }

    // check for fieldId in parent window and self window
    if (parentQueryParams.has('fieldId')) {
      setFieldId(parentQueryParams.get('fieldId') as string);
    }

    if (selfQueryParams.has('fieldId')) {
      setFieldId(selfQueryParams.get('fieldId') as string);
    }

    // check for savedViewId in parent window and self window
    if (parentQueryParams.has('savedViewId')) {
      setSavedViewId(parentQueryParams.get('savedViewId') as string);
    }

    if (selfQueryParams.has('savedViewId')) {
      setSavedViewId(selfQueryParams.get('savedViewId') as string);
    }

    // check for unsavedView in parent window and self window
    if (parentQueryParams.has('unsavedView')) {
      setUnsavedViewId(parentQueryParams.get('unsavedView') as string);
    }

    if (selfQueryParams.has('unsavedView')) {
      setUnsavedViewId(selfQueryParams.get('unsavedView') as string);
    }

    if (selfQueryParams.has('referencing')) {
      setReferencing(true);
    }
  }, [parentQueryParams, selfQueryParams]);

  useEffect(() => {
    getContentData();
    getParentContentId();
  }, [getContentData, getParentContentId]);

  if (!isDatabaseContent(value)) {
    throw new Error('Missing required query params');
  }

  return (
    <DatabaseContentContext.Provider value={value}>
      {children}
    </DatabaseContentContext.Provider>
  );
};
